import { DEFAULT_VIEW, type ViewState } from "./router";
import type { LayerName, MeasureId } from "../map/layers/types";

export type MapEvents = {
  "view:change": ViewState;
  "entity:select": string | null;
  "layer:toggle": { name: LayerName; on: boolean };
  "measure:change": { layer: LayerName; measure: MeasureId };
};

type Handler<K extends keyof MapEvents> = (payload: MapEvents[K]) => void;

const handlers: { [K in keyof MapEvents]: Set<Handler<K>> } = {
  "view:change": new Set(),
  "entity:select": new Set(),
  "layer:toggle": new Set(),
  "measure:change": new Set(),
};

let view: ViewState = DEFAULT_VIEW;

export const MapStore = {
  on<K extends keyof MapEvents>(event: K, handler: Handler<K>): () => void {
    handlers[event].add(handler);
    return () => handlers[event].delete(handler);
  },
  emit<K extends keyof MapEvents>(event: K, payload: MapEvents[K]): void {
    for (const h of handlers[event]) h(payload);
  },
  getView(): ViewState {
    return view;
  },
  setView(v: ViewState): void {
    view = v;
    for (const h of handlers["view:change"]) h(v);
  },
  setActiveLayer(name: LayerName | null): void {
    const prev = view.layers[0] ?? null;
    const nextLayers: LayerName[] = name === null ? [] : [name];
    view = { ...view, layers: nextLayers };
    for (const h of handlers["view:change"]) h(view);
    if (prev && prev !== name) {
      for (const h of handlers["layer:toggle"]) h({ name: prev, on: false });
    }
    if (name && name !== prev) {
      for (const h of handlers["layer:toggle"]) h({ name, on: true });
    }
  },
  setMeasure(layer: LayerName, measure: MeasureId): void {
    const measures = { ...view.measures, [layer]: measure };
    view = { ...view, measures };
    for (const h of handlers["view:change"]) h(view);
    for (const h of handlers["measure:change"]) h({ layer, measure });
  },
};
